import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { advertisingAccountsEndPointLogic } from "../../../../logic/PaidTrafficManagement/AdvertisingAccounts/AdvertisingAccounts/advertisingAccountsEndPointLogic";
import { selectUserToken } from "../../../../features/userSlice";
import { useSelector } from "react-redux";
// import LinearProgressWithLabel from "./AuctionInsightProgressbar";
interface AuctionInsightsProps {
  open: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  advertisingAccounts?: any;
}

export default function AuctionInsights({
  open,
  handleClose,
  handleOpen,
}: AuctionInsightsProps) {
  const oauthToken = useSelector(selectUserToken);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [, setErrorText] = useState<string | null>(null);
  const [, setSuccessText] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [, setUploadSummary] = useState({
    fetched: 0,
    updated: 0,
    ignored: 0,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const userData = useSelector(
    (state: any) => state.user?.userData?.data?.user_details
  );
  const fullName = userData
    ? `${userData.first_name} ${userData.last_name}`
    : "Username";

  const ContentItems = [
    { label: "Google", value: "GOOGLE" },
    { label: "Bing", value: "BING" },
  ];
  // const contentItems = useMemo(() => {
  //   const uniqueSources = new Set(
  //     advertisingAccounts.map(
  //       (account: { traffic_source: string }) => account.traffic_source
  //     )
  //   );
  //   return Array.from(uniqueSources).map((source) => ({ label: source }));
  // }, [advertisingAccounts]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        setFileError("Please upload a valid CSV file.");
        setSelectedFile(null);

        // Reset input field if an invalid file is selected
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setFileError(null);
        setSelectedFile(file);
      }
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setFileError(null);

    // Clear the input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Function to trigger file input click
  const handleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const resetFields = () => {
    setSelectedFile(null);
    setFileError(null);
    setSelectedSource(null);
    setErrorText(null);
    setSuccessText(null);

    // Reset file input field
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setErrorText("Please select a file before submitting.");
      return;
    }
    if (!selectedSource || !selectedSource.label) {
      setErrorText("Please select an advertising source.");
      return;
    }
    setUploading(true);

    // setProgress(0);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("advertising_system", selectedSource.value);

    try {
      const response: any =
        await advertisingAccountsEndPointLogic.createAuctionInsight(
          oauthToken,
          formData,
          setErrorText,
          setSuccessText
        );

      if (response.status === 200) {
        // setProgress(100);
        setTimeout(() => {
          handleCancel();
          resetFields();
          setUploading(false);
          // setProgress(0);
          setShowSuccessDialog(true);
        }, 1000);
        if (response.status === 200) {
          setUploadSummary({
            fetched:
              response?.data?.data?.processing_summary?.records_added || 0,
            updated:
              response?.data?.data?.processing_summary?.records_updated || 0,
            ignored:
              response?.data?.data?.processing_summary?.records_failed || 0,
          });
        }
      } else {
        setErrorText("Failed to create auction insights: " + response.reason);

        handleOpen();
        setUploading(false);
      }
    } catch (error: any) {
      setErrorText(
        "Failed to create auction insights: " +
          (error?.parsedText || "Unknown error occurred.")
      );

      handleOpen();
      setUploading(false);
    }
  };

  const handleCancel = () => {
    resetFields();
    handleClose(); // Close the dialog
  };

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
        <DialogTitle sx={{ padding: "16px 24px 0px 24px" }}>
          {"Add Auction Insights"}
        </DialogTitle>
        <DialogContent
          sx={{ padding: "16px 24px", paddingTop: "16px !Important" }}
        >
          <Grid container spacing={2}>
            <Grid size={6}>
              <Autocomplete
                options={ContentItems}
                fullWidth
                disablePortal={false}
                value={selectedSource}
                onChange={(_, newValue) => setSelectedSource(newValue)}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option: any) => option.label} // Ensures the correct type
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Advertising source"
                    size="small"
                    required
                    color="secondary"
                    placeholder="Please select a source from the list"
                  />
                )}
              />
            </Grid>
            <Grid size={5}>
              <TextField
                fullWidth
                label="Auction Insights File"
                size="small"
                color="secondary"
                helperText={fileError || "File must be in a CSV format"}
                placeholder="Please upload a file from your computer"
                value={selectedFile ? selectedFile.name : ""}
                error={!!fileError}
                InputProps={{
                  readOnly: true,
                  endAdornment: selectedFile ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearFile}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid size={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <IconButton
                  color="secondary"
                  size="small"
                  onClick={handleIconClick}
                >
                  <FileUploadOutlinedIcon fontSize="medium" color="secondary" />
                  <input
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            sx={{ color: "#00000099" }}
            justifyContent="flex-start"
          >
            <Typography fontSize={12}>Both fields are mandatory.</Typography>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: "0px 24px 24px 24px" }}>
          <Button color="secondary" onClick={handleCancel}>
            CANCEL
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading || !selectedFile || !selectedSource}
            startIcon={
              uploading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {uploading ? "Adding..." : "Add Insights"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent sx={{ textAlign: "center", padding: "32px 24px 15px" }}>
          <Typography variant="h4">Hi {fullName}!</Typography>
          <Typography variant="h4">
            We received your data upload request.
          </Typography>
          <Typography
            variant="h6"
            sx={{ marginTop: 2, color: "textSecondary" }}
          >
            Your task has been queued. Once the process is completed, you will
            receive an email from us!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", paddingBottom: "24px" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#9C27B0",
              color: "#fff",
              borderRadius: "4px",
              padding: "8px 24px",
              textTransform: "uppercase",
            }}
            onClick={() => setShowSuccessDialog(false)}
          >
            Thank You!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
